import React, { Component, Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '../Button';
import { withContactLink } from './contactLink';

/*
 * Dialog bodies load on demand (bundle budget): the enquiry form and the example offer,
 * with the Radix dialog they sit in, are separate chunks fetched on the first open (or
 * earlier, when a trigger is hovered or focused: `preload()`). The triggers, the provider
 * and this file stay in the main bundle and are small.
 */

/**
 * A lazily loaded component that can be preloaded and retried after a failed chunk load:
 * → { preload(), Component, reset() }. `Component` is the loaded component itself once the
 * chunk has arrived (no suspense, no loading flash after a preload), else a React.lazy;
 * `reset()` gives a fresh React.lazy after a failed load, so the next render retries.
 */
export function lazyDialog(loader) {
  let promise = null;
  let loaded = null;
  const load = () => {
    if (!promise) {
      promise = loader().then(
        (mod) => {
          loaded = mod.default;
          return mod;
        },
        (err) => {
          promise = null;
          throw err;
        },
      );
    }
    return promise;
  };
  let lazy = React.lazy(load);
  return {
    preload() {
      load().catch(() => {});
    },
    get Component() {
      return loaded || lazy;
    },
    reset() {
      lazy = React.lazy(load);
    },
  };
}

/** The backdrop of the pending states (same look as SiteDialog's): Escape and a click outside close. */
function PendingOverlay({ onClose, children }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="site-light fixed inset-0 z-[80] grid place-items-center bg-navy/70 px-[15px] backdrop-blur-[5px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeRef.current();
      }}
      data-site-dialog-pending=""
    >
      <div className="max-w-[600px] border border-navy/25 bg-brand-paper px-[22px] py-5 font-body text-navy shadow-[8px_10px_0] shadow-navy/50 nav:px-[35px]">
        {children}
      </div>
    </div>,
    document.body,
  );
}

/**
 * While a dialog's chunk loads (first open, no preload yet): a small "Loading…" panel on
 * the dialog backdrop. Focus stays on the trigger; the dialog moves it when it mounts.
 */
export function DialogLoading({ onClose }) {
  return (
    <PendingOverlay onClose={onClose}>
      <p role="status" className="m-0 font-label text-xs uppercase leading-[1.65] tracking-[0.07em]">
        Loading…
      </p>
    </PendingOverlay>
  );
}

/** A chunk that failed to load: the message and a Close button (focused). */
export function DialogLoadError({ message, onClose }) {
  const buttonRef = useRef(null);
  useEffect(() => {
    if (buttonRef.current) buttonRef.current.focus();
  }, []);
  return (
    <PendingOverlay onClose={onClose}>
      <div role="alert" className="max-w-[26rem]">
        <p className="m-0 text-[0.9375rem] leading-relaxed">{withContactLink(message)}</p>
        <Button ref={buttonRef} variant="secondary" size="sm" arrow={false} className="mt-4" onClick={onClose}>
          Close
        </Button>
      </div>
    </PendingOverlay>
  );
}

/** Catches a failed chunk load under it and shows DialogLoadError while `open`. */
export class DialogBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    // A failed chunk load (offline, a new deploy): shown in the dialog, nothing else to do.
  }

  render() {
    const { failed } = this.state;
    const { open, message, onDismiss, children } = this.props;
    if (failed) return open ? <DialogLoadError message={message} onClose={onDismiss} /> : null;
    return children;
  }
}

/**
 * Renders a lazily loaded dialog body from its first open on (so its state, e.g. a typed
 * enquiry, survives closing): `render(Body)` returns the element to show. The first open
 * shows DialogLoading until the chunk arrives; a failed load shows DialogLoadError and the
 * next open tries again.
 *
 * Props: `dialog` (from lazyDialog), `open`, `onClose()` (close from the pending states;
 * return focus there), `errorMessage`, `render(Body)`.
 */
export function LazyDialog({ dialog, open, onClose, errorMessage, render }) {
  const [active, setActive] = useState(open);
  // The component type is fixed per activation: switching from the React.lazy to the loaded
  // component later would remount the body and lose its state.
  const bodyRef = useRef(null);
  if (open && !active) setActive(true);
  if (!active && !open) return null;
  if (!bodyRef.current) bodyRef.current = dialog.Component;
  const Body = bodyRef.current;
  return (
    <DialogBoundary
      open={open}
      message={errorMessage}
      onDismiss={() => {
        dialog.reset();
        bodyRef.current = null;
        setActive(false);
        onClose();
      }}
    >
      <Suspense fallback={open ? <DialogLoading onClose={onClose} /> : null}>{render(Body)}</Suspense>
    </DialogBoundary>
  );
}
